/**
 * i18next bootstrap — imported once from `src/main.tsx` BEFORE the React
 * tree renders. The pre-paint inline script in `index.html` has already
 * applied `<html lang>` + `<html dir>` from localStorage, so first paint
 * sees the right direction. This module wires the runtime: lazy
 * namespace loading, ICU MessageFormat, custom formatters, language
 * detection.
 *
 * Source-of-truth precedence for the active locale (mirrors the BE
 * `resolveLocale` precedence so server + client agree):
 *
 *   1. localStorage `locale` (set by the pre-paint script and by
 *      `useLocale().setLocale`).
 *   2. `window.__INITIAL_LOCALE__` (server-injected — unused for the SPA
 *      today; kept as a hook for future SSR).
 *   3. `navigator.language` first 2-letter prefix matching a supported
 *      locale.
 *   4. `'en'` default.
 *
 * The `useMe()` hook reconciles BE-as-truth on first /api/me load — if
 * the persisted user locale differs from the cached one, `useLocale()`
 * resyncs DOM + localStorage to the BE value.
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ICU from 'i18next-icu'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'

import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  I18N_NAMESPACES,
  LOCALES,
  PSEUDO_LOCALE,
} from './config'
import { registerFormatters } from './formatters'
// Type-only import: registers the `CustomTypeOptions` declaration merge so
// `t()` calls compile-check against the canonical en/ catalog shape.
import './types'

const supportedLngs: readonly string[] =
  import.meta.env.DEV === true ? [...LOCALES, PSEUDO_LOCALE] : LOCALES

/**
 * Catalog backend — Vite-friendly dynamic imports. Each (locale,
 * namespace) pair is its own chunk, so the SPA only fetches what's
 * needed for the active locale + visited features.
 *
 * The pseudo-locale is generated at build time from `en/` by
 * `scripts/generate-pseudo-locale.mjs`; the dynamic import below picks
 * it up from the same `./locales/en-XA/` directory once it exists.
 * Until the script runs, en-XA imports fall through to the en fallback.
 */
const catalogBackend = resourcesToBackend((lng: string, ns: string) =>
  import(`./locales/${lng}/${ns}.json`).catch(() => {
    // Missing namespace JSON for this locale falls back to en at the
    // i18next layer via fallbackLng. Resolving with an empty object
    // here keeps the load promise from rejecting.
    return {}
  }),
)

const initI18n = (): typeof i18n => {
  if (i18n.isInitialized) return i18n

  // Register formatters BEFORE init so they're available on first render.
  // (`useSuspense: false` means components render before the init promise
  // resolves; deferring registration to `.then()` causes ICU templates
  // like `{createdAt, relativeTime}` to render as literals on the first paint.)
  i18n
    .use(ICU)
    .use(catalogBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // ---- Locale set ---------------------------------------------------
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: supportedLngs as string[],
      // Region-stripping happens in the DETECTOR (convertDetectedLanguage
      // below), NOT via `load: 'languageOnly'` — languageOnly strips the
      // region from EVERY code with no exemption mechanism, which silently
      // normalized the pseudo-locale `en-XA` to `en`: Pseudo (dev) set
      // localStorage + <html lang> but i18next loaded the plain en
      // catalogs, defeating the extraction-miss QA walk on every ticket.
      // `currentOnly` loads exactly what detection resolved.
      load: 'currentOnly',
      nonExplicitSupportedLngs: false,
      cleanCode: true,
      lowerCaseLng: false,

      // ---- Namespaces ---------------------------------------------------
      ns: I18N_NAMESPACES as unknown as string[],
      defaultNS: DEFAULT_NAMESPACE,
      partialBundledLanguages: true,

      // ---- Detection ----------------------------------------------------
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: 'locale',
        caches: ['localStorage'],
        // Match the BE precedence: localStorage is the FE's persisted
        // signal; navigator.language is the cold-start fallback.
        // Regional variants route to their base language here (navigator
        // `es-MX` / `es-419` → `es`, `en-US` → `en`) so a Spanish-region
        // first visit still lands on the es catalogs under
        // `load: 'currentOnly'`. The pseudo-locale is the ONE exemption —
        // its region subtag is what distinguishes it from `en`.
        convertDetectedLanguage: (lng) =>
          lng === PSEUDO_LOCALE ? lng : (lng.split('-')[0] ?? DEFAULT_LOCALE),
      },

      // ---- React-i18next ------------------------------------------------
      react: {
        // useSuspense:false keeps the existing TanStack Query loading
        // patterns the canonical loading surface — i18n loads its
        // namespaces in parallel with /api/me, no nested Suspense.
        useSuspense: false,
      },

      // ---- Behavior -----------------------------------------------------
      // Throw on missing keys in dev so extraction misses surface during
      // Playwright runs; warn-only in prod so a stray missing key doesn't
      // crash the whole render tree.
      saveMissing: false,
      missingKeyHandler:
        import.meta.env.DEV === true
          ? (lngs, ns, key) => {
              // eslint-disable-next-line no-console
              console.warn(`[i18n] missing key: ${ns}:${key} (locale=${lngs.join(',')})`)
            }
          : undefined,

      // The ICU plugin handles plural/select/selectOrdinal natively; we
      // only need basic interpolation here for non-ICU strings.
      interpolation: {
        escapeValue: false, // React already escapes JSX
      },

      returnNull: false,
      returnEmptyString: false,
    })
    .then(() => {
      // Safety net: if ICU plugin created the formatter service AFTER
      // the synchronous attempt below, the .then() catches it.
      registerFormatters(i18n)
    })

  // The formatter service usually exists synchronously after `.init()`
  // returns. Register here so the first render of a `{x, formatter}` ICU
  // template resolves on first paint (the .then() above is the safety net
  // in case the formatter service isn't ready yet at this point).
  registerFormatters(i18n)

  return i18n
}

export default initI18n()
export { i18n }
